<template>
  <table v-if="corporations.length > 0" :class="$style.corporations">
    <thead>
      <tr
        :class="[
          $style.corporations__list,
          $style['corporations__list--header'],
        ]"
      >
        <th
          :class="[
            $style.corporations__cell,
            $style['corporations__cell--header'],
          ]"
        >
          <p>保険会社</p>
        </th>
        <th
          :class="[
            $style.corporations__cell,
            $style['corporations__cell--header'],
          ]"
        >
          <p>パンフレット名</p>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr
        :class="[$style.corporations__list]"
        v-for="corporation in corporations"
        :key="corporation.id"
      >
        <td :class="[$style.corporations__cell]">
          <img
            v-if="idToLogo[corporation.id]"
            :src="idToLogo[corporation.id].src"
            :class="[$style.corporations__logo, idToLogo[corporation.id].class]"
          />
          <p
            :class="[
              $style.corporations__corporationName,
              {
                [$style['corporations__corporationName--noImg']]:
                  !idToLogo[corporation.id],
              },
            ]"
          >
            {{ corporation.name }}
          </p>
        </td>
        <td :class="[$style.corporations__cell]">
          <ul :class="$style.versions">
            <li
              v-for="document in corporation.documents"
              :key="document.documentVersionId"
            >
              <div
                @click="clickItem(document.documentVersionId, document.page)"
                :class="$style.version"
              >
                <p :class="$style.version__title">{{ document.title }}</p>
                <p v-if="document.publishedAt" :class="$style.version__date">
                  最終更新日:
                  {{ formatDate(document.publishedAt) }}
                </p>
              </div>
            </li>
          </ul>
        </td>
      </tr>
    </tbody>
  </table>
  <div v-else :class="$style.empty">
    <OkanekoSorry :class="$style.empty__img" />
    <p>該当の資料は見つかりませんでした</p>
  </div>
</template>

<script setup lang="ts">
import { useCssModule } from 'vue'
import OkanekoSorry from '@/assets/images/okaneko-sorry.svg?component'
import { format } from 'date-fns'
import fwdLogo from '@/assets/images/logos/external/fwd.jpg'
import himawariLogo from '@/assets/images/logos/external/himawari.jpg'
import sbiLogo from '@/assets/images/logos/external/sbi.jpg'
import hanasakuLogo from '@/assets/images/logos/external/hanasaku.svg?url'
import aioiLogo from '@/assets/images/logos/external/aioi.svg?url'

export type Document = {
  documentVersionId: string
  title: string
  page: number
  publishedAt?: string
}

export type Corporation = {
  id: string
  name: string
  documents: Document[]
}

const style = useCssModule()

const idToLogo: { [key: string]: { src: string; class: string } } = {
  1: { src: fwdLogo, class: style['corporations__logo--fwd'] },
  4: { src: himawariLogo, class: style['corporations__logo--himawari'] },
  5: { src: hanasakuLogo, class: style['corporations__logo--hanasaku'] },
  8: { src: aioiLogo, class: style['corporations__logo--aioi'] },
  9: { src: sbiLogo, class: style['corporations__logo--sbi'] },
}

defineProps<{
  corporations: Corporation[]
}>()

const emit = defineEmits<{
  (
    event: 'click',
    value: { documentVersionId: Document['documentVersionId']; page: number }
  ): void
}>()

const clickItem = async (
  documentVersionId: Document['documentVersionId'],
  page: number
) => {
  emit('click', { documentVersionId, page })
}

const formatDate = (value: string) => format(new Date(value), 'yyyy/M/d H:mm')
</script>

<style lang="scss" module>
.corporations {
  $title-width: 300px;

  width: 100%;
  border-spacing: 0;
  border: 2px solid $color-border-low-emphasis;
  border-radius: 6px;

  &__logo {
    height: 40px;
  }

  &__cell {
    padding: 15px 20px;
    text-align: left;
    border-top: 1px solid $color-border-low-emphasis;

    &--header {
      border-top: none;
    }
  }

  &__corporationName {
    font-size: 12px;

    &--noImg {
      font-weight: bold;
      font-size: 16px;
    }
  }

  &__list {
    align-items: left;

    &--header {
      background-color: $color-surface-secondary;
      font-weight: bold;
    }
  }
}

.versions {
  width: 100%;

  li {
    width: 100%;

    &:nth-child(n + 2) {
      margin-top: 6px;
    }
  }
}

.version {
  width: 100%;
  display: flex;
  background-color: $color-surface-secondary;
  padding: 8px 20px;
  border-radius: 6px;
  align-items: center;
  justify-content: space-between;
  color: $color-text-and-object-accent-primary;
  font-size: 14px;

  &:hover {
    background-color: $color-surface-accent-primary-light;
  }

  &__date {
    font-size: 10px;
    color: $color-text-and-object-high-emphasis;
  }
}

.empty {
  background-color: $color-surface-secondary;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  & > *:nth-child(n + 2) {
    margin-top: 20px;
  }
}
</style>
